package info.rezident.app.ui.components.animations

import androidx.compose.runtime.Composable
import androidx.compose.runtime.remember
import com.varabyte.kobweb.compose.dom.ElementRefScope
import com.varabyte.kobweb.compose.dom.disposableRef
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.ColumnScope
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.core.isExporting
import com.varabyte.kobweb.core.rememberPageContext
import info.rezident.app.core.AnimationPlaybackControls
import info.rezident.app.core.animate
import info.rezident.app.core.animateElements
import info.rezident.app.core.animateValue
import info.rezident.app.core.hover
import info.rezident.app.core.inView
import info.rezident.app.core.press
import info.rezident.app.core.stagger
import kotlinx.browser.window
import org.w3c.dom.Element
import org.w3c.dom.HTMLElement
import org.w3c.dom.asList
import kotlin.js.Json
import kotlin.js.json
import kotlin.math.roundToInt

// Starts fast and settles gently, used by every entrance effect
val RevealEase = arrayOf(0.22, 1.0, 0.36, 1.0)
const val REVEAL_DURATION_SECONDS = 0.6
const val REVEAL_OFFSET_PX = 24.0

// How much of an element must be on screen before its entrance effect plays
private const val REVEAL_VISIBLE_AMOUNT = 0.2

fun springTransition(delay: Double = 0.0): Json =
    json("type" to "spring", "stiffness" to 380, "damping" to 22, "delay" to delay)

fun prefersReducedMotion(): Boolean = window.matchMedia("(prefers-reduced-motion: reduce)").matches

/**
 * Whether decorative effects should play.
 *
 * They are skipped while Kobweb exports the page, so the pre-rendered HTML (what people see before the scripts load,
 * and what crawlers read) always holds the final, visible content. They are also skipped for people who asked their
 * system for reduced motion.
 */
@Composable
fun rememberMotionEnabled(): Boolean {
    val ctx = rememberPageContext()
    return remember { !ctx.isExporting && !prefersReducedMotion() }
}

/**
 * Returns a ref that runs [setup] once the element is in the page, and the cleanup it returns once the element leaves.
 * Returns null when effects are off, so the result can go straight into a Kobweb `ref` parameter.
 */
@Composable
fun <T : Element> rememberMotionRef(setup: (T) -> () -> Unit): ElementRefScope<T>? {
    if (!rememberMotionEnabled()) return null

    return disposableRef { element ->
        val cleanup = setup(element)
        onDispose { cleanup() }
    }
}

private fun Element.isInViewport(): Boolean {
    val rect = getBoundingClientRect()
    return rect.top < window.innerHeight && rect.bottom > 0
}

/**
 * Plays an entrance effect the first time [element] scrolls into view, and returns a function that cancels it.
 *
 * [prepare] puts the element in the effect's starting state, and [play] animates it to its final state.
 * Elements that are already on screen when they appear are left as they are: they were already visible in the
 * pre-rendered HTML, so hiding them only to animate them back in would make them blink.
 *
 * The effect starts once [amount] of the element's height (0.2 = 20%) is on screen.
 */
fun playOnFirstView(
    element: Element,
    prepare: () -> Unit,
    play: () -> AnimationPlaybackControls,
    amount: Double = REVEAL_VISIBLE_AMOUNT
): () -> Unit {
    if (element.isInViewport()) return {}

    prepare()
    var animation: AnimationPlaybackControls? = null
    val stopWatching = inView(element, { _, _ -> animation = play() }, json("amount" to amount))

    return {
        stopWatching()
        animation?.stop()
    }
}

private fun HTMLElement.hideForReveal(x: Double, y: Double, scale: Double) {
    style.opacity = "0"
    style.transform = "translateX(${x}px) translateY(${y}px) scale($scale)"
}

private fun revealKeyframes(x: Double, y: Double, scale: Double): Json = json(
    "opacity" to arrayOf(0, 1),
    "x" to arrayOf(x, 0),
    "y" to arrayOf(y, 0),
    "scale" to arrayOf(scale, 1)
)

/**
 * Fades [element] in from an offset of [x], [y] and a size of [scale] the first time it scrolls into view,
 * over [duration] seconds (springs set their own pace), once [amount] of its height is on screen.
 */
fun revealOnScroll(
    element: HTMLElement,
    x: Double = 0.0,
    y: Double = REVEAL_OFFSET_PX,
    scale: Double = 1.0,
    delay: Double = 0.0,
    spring: Boolean = false,
    duration: Double = REVEAL_DURATION_SECONDS,
    amount: Double = REVEAL_VISIBLE_AMOUNT
): () -> Unit = playOnFirstView(
    element,
    prepare = { element.hideForReveal(x, y, scale) },
    play = {
        val transition = if (spring) {
            springTransition(delay)
        } else {
            json("duration" to duration, "delay" to delay, "ease" to RevealEase)
        }
        animate(element, revealKeyframes(x, y, scale), transition)
    },
    amount = amount
)

/** Fades the children of [container] in one after the other, the first time the container scrolls into view. */
fun revealChildrenOnScroll(
    container: HTMLElement,
    staggerSeconds: Double,
    y: Double = REVEAL_OFFSET_PX,
    delay: Double = 0.0
): () -> Unit {
    val children = container.children.asList().filterIsInstance<HTMLElement>().toTypedArray()

    return playOnFirstView(
        container,
        prepare = { children.forEach { it.hideForReveal(x = 0.0, y = y, scale = 1.0) } },
        play = {
            animateElements(
                children,
                revealKeyframes(x = 0.0, y = y, scale = 1.0),
                json(
                    "duration" to REVEAL_DURATION_SECONDS,
                    "delay" to stagger(staggerSeconds, json("startDelay" to delay)),
                    "ease" to RevealEase
                )
            )
        }
    )
}

/** Counts up from 0 to [target] the first time [element] scrolls into view, reporting each step to [onUpdate]. */
fun countUpOnScroll(element: Element, target: Int, delay: Double = 0.0, onUpdate: (Int) -> Unit): () -> Unit =
    playOnFirstView(
        element,
        prepare = { onUpdate(0) },
        play = {
            animateValue(
                0.0,
                target.toDouble(),
                json(
                    "duration" to 1.6,
                    "delay" to delay,
                    "ease" to "easeOut",
                    "onUpdate" to { value: Double -> onUpdate(value.roundToInt()) }
                )
            )
        }
    )

/** Makes [element] grow slightly while hovered with a mouse and shrink while pressed, with a little spring. */
fun attachSpringPress(element: Element): () -> Unit {
    val stopHover = hover(element) { target, _ ->
        animate(target, json("scale" to 1.03), springTransition())
        val onEnd: (dynamic) -> Unit = { animate(target, json("scale" to 1), springTransition()) }
        onEnd
    }

    val stopPress = press(element) { target, _ ->
        animate(target, json("scale" to 0.96), springTransition())
        val onEnd: (dynamic) -> Unit = { event ->
            // Return to the hover size if the mouse is still over it, touches have no hover to go back to
            val stillHovered = event.pointerType == "mouse" && target.matches(":hover")
            animate(target, json("scale" to if (stillHovered) 1.03 else 1), springTransition())
        }
        onEnd
    }

    return {
        stopHover()
        stopPress()
    }
}

/** A column whose content fades in from an offset of [x], [y] and a size of [scale] when it first scrolls into view. */
@Composable
fun Reveal(
    modifier: Modifier = Modifier,
    x: Double = 0.0,
    y: Double = REVEAL_OFFSET_PX,
    scale: Double = 1.0,
    delay: Double = 0.0,
    duration: Double = REVEAL_DURATION_SECONDS,
    amount: Double = REVEAL_VISIBLE_AMOUNT,
    horizontalAlignment: Alignment.Horizontal = Alignment.Start,
    content: @Composable ColumnScope.() -> Unit
) {
    Column(
        modifier,
        horizontalAlignment = horizontalAlignment,
        ref = rememberMotionRef { element ->
            revealOnScroll(element, x, y, scale, delay, duration = duration, amount = amount)
        },
        content = content
    )
}
